import constants from './indesignEditor/constants';
import {
	ActionEventType,
	editedElementClassName,
	LinkParam,
	LinkType,
	PAGE_CONTENT_CLASSNAME,
	PdfImportingStatus,
	subScriptClassName,
	superScriptClassName,
	VerticalAlignClassName,
} from '../constants';
import UrlUtils from '../../../utils/url';
import { Api } from '../../../typings/api';
import Utils from '../../../utils';
import store from '../../../store';
import UserAgentUtils from '../../../utils/userAgent';
import SelectionManager from '../../../utils/selectionManager';

interface ElementSize {
	width: number;
	height: number;
}

interface StyleProps {
	transform: string | null;
}

interface OptimizedImageData {
	width: number;
	height: number;
	src: string;
}

export type LinkData = LinkUrlData | LinkEmailData | LinkGoToPageData | LinkGoToChapterIdData | LinkPopupData;

export type ActionEventData = PopupActionEventData | GoToPageActionEventData | GoToChapterIdActionEventData | MailtoActionEventData;

interface MailtoActionEventData {
	type: ActionEventType.mailto;
	params: {
		email: string;
	};
}

interface GoToPageActionEventData {
	type: ActionEventType.goToPage;
	params: {
		page: number;
		spread?: number;
	};
}

interface GoToChapterIdActionEventParams {
	chapterId: string;
	method?: string;
	tooltip?: string;
	isTooltipActive?: boolean;
}

interface GoToChapterIdActionEventData {
	type: ActionEventType.goToChapterId;
	params: GoToChapterIdActionEventParams;
}

export interface PopupActionEventData {
	type: ActionEventType.popup;
	params: {
		animation: null;
		appearance: null | {};
		assetDriveItem: Api.AspDriveResourceItem;
		enable: boolean;
		header: null;
		width: string;
		height: string;
		isTooltipActive?: boolean;
		pageNumber?: number;
		param: string;
		subAssetDriveItem: Api.AspDriveResourceItem | null;
		theme: string;
		tooltip?: string;
	};
}

export interface LinkUrlData {
	type: LinkType.url;
	data: LinkUrlParams;
	originalParams?: PopupActionEventData;
}

export interface LinkEmailData {
	type: LinkType.mailto;
	data: LinkUrlParams;
	originalParams?: PopupActionEventData;
}

export interface LinkGoToPageData {
	type: LinkType.goToPage;
	data: LinkUrlParams;
	originalParams?: PopupActionEventData;
}

export interface LinkGoToChapterIdData {
	type: LinkType.goToChapterId;
	data: LinkGoToChapterIdParams;
	originalParams?: GoToChapterIdActionEventData;
}

export interface LinkPopupData {
	type: LinkType.popup;
	data: LinkPopupParams;
	originalParams?: PopupActionEventData;
}

interface LinkUrlParams {
	href: string;
	correctedHref?: string;
}

interface LinkPopupParams {
	item: Api.AspDriveResourceItem;
	page?: number;
}

interface LinkGoToChapterIdParams {
	href: string;
}

export const getLinkUrlData = (url: string): LinkUrlData => {
	return {
		type: LinkType.url,
		data: {
			href: url,
		}
	};
};

const defaultLinkData: LinkUrlData = getLinkUrlData('');

export enum TooltipAction {
	SHOW = 'showTooltip',
	HIDE = 'hideTooltip',
}

const getSpreadPages = (
	displayMode: Api.PublicationDisplayMode,
	firstFrameMode: Api.PublicationFirstFrameMode,
	possiblePage: number,
	totalPages: number,
	showAlonePages: number[],
	pageDimensions?: { width: number, height: number },
): number[] => {
	const spreadPossiblePages = [possiblePage, possiblePage + 1];
	const spreadPageNumber = getSpreadPageNumber(displayMode, firstFrameMode, spreadPossiblePages, totalPages, showAlonePages, pageDimensions);

	return spreadPageNumber === 1 ? [possiblePage] : spreadPossiblePages;
};

const getSpreadPageNumber = (
	displayMode: Api.PublicationDisplayMode,
	firstFrameMode: Api.PublicationFirstFrameMode,
	possiblePages: number[],
	totalPages: number,
	showAlonePages: number[],
	pageDimensions?: { width: number, height: number },
): number => {
	const hasOutOfBound = possiblePages[0] < 1 || possiblePages[1] > totalPages;
	const hasCover = firstFrameMode === Api.PublicationFirstFrameMode.SINGLE && possiblePages.includes(1);
	const hasShowAlonePages = showAlonePages.some(showAlonePage => possiblePages.includes(showAlonePage));
	const isWidePage = pageDimensions && pageDimensions.width > pageDimensions.height;

	if (
		![Api.PublicationDisplayMode.AUTO, Api.PublicationDisplayMode.FLIPBOOK].includes(displayMode) ||
		hasOutOfBound ||
		hasCover ||
		hasShowAlonePages ||
		isWidePage
	) {
		return 1;
	}

	return 2;
};

export const getPageSpreads = (
	displayMode: Api.PublicationDisplayMode,
	firstFrameMode: Api.PublicationFirstFrameMode,
	totalPages: number,
	pagesData: { showAlone: boolean }[],
	pageDimensions?: { width: number, height: number },
): number[][] => {
	const showAlonePages: number[] = pagesData.reduce((acc: number[], current, index) => {
		if (current.showAlone) {
			acc.push(index + 1);
		}

		return acc;
	}, []);
	const spreads: number[][] = [];
	let currentPage = 1;

	while (currentPage <= totalPages) {
		const spreadPages = getSpreadPages(displayMode, firstFrameMode, currentPage, totalPages, showAlonePages, pageDimensions);
		spreads.push(spreadPages);
		currentPage += spreadPages.length;
	}

	return spreads;
};

export const getSelectedPages = (selectedSpreadIndices: number[], spreads: number[][]): number[] => (
	selectedSpreadIndices.reduce((acc: number[], current: number) => {
		acc = acc.concat(spreads[current]);
		return acc;
	}, [])
);

export const getActiveSpreadIndexAfterPagesUpdate = (
	spreadIndex: number,
	spreads: number[][],
	displayMode: Api.PublicationDisplayMode,
	pagesData: { showAlone: boolean }[],
): number => {
	const isLastSpread = spreadIndex === spreads.length - 1;

	if (!isLastSpread) {
		return spreadIndex + 1;
	}

	const lastSpread = spreads[spreadIndex];
	const isShowAlonePage = lastSpread.some(page => (
		pagesData[page - 1] && pagesData[page - 1].showAlone
	));

	return (
		displayMode === Api.PublicationDisplayMode.AUTO &&
		lastSpread.length === 1 &&
		!isShowAlonePage
	)
		? spreadIndex
		: spreadIndex + 1;
};

export const getPageHtmlPath = (path: string, page: number) => path.replace('%{pageNumber}', page.toString());

export const getNumberFromString = (str: string): number => {
	const regExp = /[+-]?\d+(\.\d+)?/g;
	const match = str.match(regExp);
	return match ? +match[0] : 0;
};

export const getRoundedValue = (value: number, decimal: number = 100): number => (
	Math.round(value * decimal) / decimal
);

export const getValueAndUnits = (value: string): { value: number, units: string } => {
	const valueWithoutUnits = getNumberFromString(value);
	const units = value.replace(valueWithoutUnits.toString(), '');

	return {
		value: valueWithoutUnits,
		units,
	};
};

export const youtubeUrl = 'https://www.youtube.com/embed/';
const youtubePreviewUrl = 'https://i.ytimg.com/vi/';
const youtubeUrlRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;

const getYoutubeVideoId = (url: string): string => {
	const match = url.match(youtubeUrlRegex);

	if (!match) {
		return '';
	}

	return match[2].split('" ')[0];
};

export const isYoutubeLink = (url: string): boolean => url.includes(youtubeUrl);

export const getYoutubeLink = (url: string): string => {
	const videoId = getYoutubeVideoId(url);
	return videoId ? `${youtubeUrl}${videoId}` : '';
};

export const getYoutubeVideoCover = (url: string): string => {
	const videoId = getYoutubeVideoId(url);
	return `${youtubePreviewUrl}${videoId}/0.jpg`;
};

export const getVideoCover = (url: string, isDefaultVideoInHtmlEditor?: boolean): string => {
	if (isDefaultVideoInHtmlEditor) {
		return `${url}-cover.jpg`;
	}

	return url.replace(/\.[^.]+$/, '/cover.jpg');
};

export const getAnimationCover = (url: string): string => {
	return url.replace(/\/index\.html+$/, '/cover.jpg');
};

export const mediaParamRegex = /\$media\$|\${media}|\$%7Bmedia%7D/;
export const imageParamRegex = /\$\{image_quality_folder}/;
export const hasMediaParam = (str: string): boolean => !!str.match(mediaParamRegex);
export const hasImageParam = (str: string): boolean => !!str.match(imageParamRegex);

const getRelUrlFromStringWithParam = (paramRegex: RegExp, replacement: string, str: string): string => {
	const match = str.match(paramRegex);
	return match ? str.replace(match[0], replacement) : str;
};

export const getRelUrlFromStringWithMediaParam = (str: string): string => {
	return getRelUrlFromStringWithParam(mediaParamRegex, '', str);
};

export const getRelUrlFromStringWithImageParam = (str: string): string => {
	return getRelUrlFromStringWithParam(imageParamRegex, 'image', str);
};

export const getSelectorStylesFromCss = (selector: string, css: string): string => {
	// (?=[\s,{:]) — positive lookahead: next char is a space, comma, '{', or ':' (for pseudo-selectors), not consumed
	const regExp = new RegExp(`${selector}(?=[\\s,{:]).*?(\\s*\\{[^}]*\\})`);
	const extraSymbolsInStylesRegExp = /{|}|\s+(?=(?:[^\'"]*[\'"][^\'"]*[\'"])*[^\'"]*$)/g;
	const match = css.match(regExp);
	return match ? match[1].replace(extraSymbolsInStylesRegExp, '') : '';
};

export const getElementSizes = (id: string, css: string): ElementSize => {
	const styles = getSelectorStylesFromCss(`#${id}`, css);
	return styles
		.split(';')
		.filter((style) => {
			const styleName = style.split(':')[0];
			return styleName === 'width' || styleName === 'height';
		})
		.reduce((acc, current) => {
			const [key, value] = current.split(':');
			acc[key] = getNumberFromString(value);
			return acc;
		}, {} as ElementSize);
};

export const getElementScale = (element: HTMLElement, css: string): number => {
	const {
		id,
		dataset: {
			[constants.animationEndStateDataAttribute]: animationClassName,
		},
	} = element;
	const styles = getSelectorStylesFromCss(id, css);
	const regExp = /scale\((.+?)\)/;

	let scale = 1;

	const stylesMatch = styles.match(regExp);
	if (stylesMatch) {
		scale = +stylesMatch[1].split(',')[0];
	}

	if (animationClassName) {
		const animationStyles = getSelectorStylesFromCss(animationClassName, css);
		const animationStylesMatch = animationStyles.match(regExp);
		if (animationStylesMatch) {
			scale = +animationStylesMatch[1].split(',')[0];
		}
	}

	return getRoundedValue(scale);
};

export const getStylesWithScaledNumbers = (stylesString: string, scale: number): string => {
	const styleNames = [
		'font-size',
		'line-height',
		'min-height',
		'padding-top',
		'letter-spacing',
	];
	const regExp = new RegExp(styleNames
		.map(styleName => `${styleName}:\\s*([+-]?\\d+\\.\\d+|[+-]?\\d+)`)
		.join('|'), 'g');

	return stylesString.replace(regExp, (match, ...groups) => {
		const index = styleNames.findIndex(styleName => match.includes(styleName));
		const size = groups[index];
		const scaledNumber = getRoundedValue(getNumberFromString(size) * scale);

		return `${styleNames[index]}:${scaledNumber}`;
	});
};

export const getScaleFromStyle = ({ transform }: StyleProps): number => {
	const regExp = /scale\((.+?)\)/;
	const match = transform && transform.match(regExp);
	return match ? +match[1] : 1;
};

export const getTransformStyle = ({ transform }: StyleProps): string => {
	const regExp = /translate\((.+?)\)/;
	const match = transform && transform.match(regExp);
	return match ? match[0] : '';
};

export const getTranslateValueFromStyle = ({ transform }: StyleProps): number[] => {
	const regExp = /translate\((.+?)\)/;
	const match = transform && transform.match(regExp);

	if (!match) {
		return [0, 0];
	}

	const value = match[1];
	return value.split(',').map(item => getNumberFromString(item));
};

export const getCorrectedLetterSpacing = (letterSpacing: string, scale: number): number => {
	const letterSpacingValue = getNumberFromString(letterSpacing);
	// We have to ignore small letter-spacing because inDesign sets it almost on each word (span element) and it will cause a huge amount of generated spans in Rich text editors
	// 2px from inDesign === 2px / 20 = 0.1px in backoffice
	const IGNORED_LETTER_SPACING_VALUE = 2;
	const isIgnoredLetterSpacing = Math.abs(letterSpacingValue) < IGNORED_LETTER_SPACING_VALUE;

	return isIgnoredLetterSpacing
		? 0
		: getRoundedValue(letterSpacingValue * scale, 10);
};

export const getTooltipAction = (action: TooltipAction, tooltip: string = ''): string => (
	// tslint:disable-next-line: max-line-length
	`javascript:document.dispatchEvent(new CustomEvent("WebPublicationAction", {"detail":{"type":"${action}","params":{"content": "${tooltip}"},"event":typeof(event) !== "undefined" ? event : undefined}})); return false;`
);

// get tooltip in case it was set in inDesign (for video)
export const getDefaultTooltip = (value: string): string => {
	if (!value) {
		return '';
	}

	const regExp = /"params":{"content": "(.+?)"}/;
	const match = value.match(regExp);
	return match ? match[1] : '';
};

export const setElementIsEdited = (element: HTMLElement): void => {
	if (!element.classList.contains(editedElementClassName)) {
		element.classList.add(editedElementClassName);
	}
};

export const setElementIsHidden = (element: HTMLElement): void => {
	element.setAttribute('hidden', 'true');
	// NB: Do not remove 'src' attribute as it breaks inDesign animations in Reader!
};

export const getUrlWithoutOrigin = (url: string, isHtmlEditor?: boolean): string => {
	let modifiedUrl = url;

	if (isHtmlEditor) {
		if (UserAgentUtils.isMobileApp) {
			const FILES_PATH = 'www/v2/';
			if (url.indexOf(FILES_PATH) !== -1) {
				modifiedUrl = url.substring(url.indexOf(FILES_PATH) + FILES_PATH.length);
			}
		} else {
			const { href, search, hash } = location;
			const origin = href.replace(`${search}${hash}`, '');
			modifiedUrl = url.replace(origin, '');
		}
	}

	const regExp = /\$media\$|\${media}|\$%7Bmedia%7D|publication\/contents/;
	const match = modifiedUrl.match(regExp);

	return match && match.index
		// we have to replace some encoded characters to its actual values for proper handling of urls in Reader
		? modifiedUrl.substring(match.index).replace(/%7B/g, '{').replace(/%7D/g, '}')
		: modifiedUrl;
};

export const getResourceUrl = (url: string, config: Api.Config, resourceId?: number, isHtmlEditor?: boolean): string => {
	const urlWithoutOrigin = getUrlWithoutOrigin(url, isHtmlEditor);

	if (isYoutubeLink(urlWithoutOrigin)) {
		return urlWithoutOrigin;
	} else if (hasMediaParam(urlWithoutOrigin) && hasImageParam(urlWithoutOrigin)) {
		const resolvedRelUrlWithMediaParam = getRelUrlFromStringWithMediaParam(urlWithoutOrigin);
		return UrlUtils.getEditorResourceUrl(config, undefined, getRelUrlFromStringWithImageParam(resolvedRelUrlWithMediaParam));
	} else if (hasMediaParam(urlWithoutOrigin)) {
		return UrlUtils.getEditorResourceUrl(config, undefined, getRelUrlFromStringWithMediaParam(urlWithoutOrigin));
	} else if (hasImageParam(urlWithoutOrigin)) {
		return UrlUtils.getEditorResourceUrl(config, resourceId, getRelUrlFromStringWithImageParam(urlWithoutOrigin));
	}

	return UrlUtils.getEditorResourceUrl(config, resourceId, urlWithoutOrigin);
};

export const getMediaUrl = (relUrl: string, iconName?: string): string => {
	if (iconName) {
		return '${media}' + iconName;
	}

	if (hasMediaParam(relUrl)) {
		return relUrl;
	}

	const endWithSlash = relUrl[relUrl.length - 1] === '/';
	const splitBySlash = relUrl.split('/');
	const idWithExtension = endWithSlash
		? splitBySlash[splitBySlash.length - 2]
		: splitBySlash[splitBySlash.length - 1];

	return '${media}' + idWithExtension;
};

export const getBorderWidth = (scale: number, minWidth: number = 2): number => (
	getRoundedValue(minWidth / scale)
);

// used for links inside text fields and images
export const getLinkTypeFromHref = (href: string, action?: string): LinkType => {
	const stringToCheck = href === 'javascript:void(0)' ? action as string : href;
	return getLinkTypeFromAction(stringToCheck);
};

// used for getting link type by its actual value (on page save)
export const getLinkTypeByValue = (value: string): LinkType => {
	if (!value || value.startsWith('http')) {
		return LinkType.url;
	}

	if (value.includes('@')) {
		return LinkType.mailto;
	}

	if (!isNaN(+value)) {
		return LinkType.goToPage;
	}

	if (value.endsWith('/')) {
		return LinkType.popup;
	}

	return LinkType.unsupported;
};

// used for getting link type by its action string (on page load)
export const getLinkTypeFromAction = (action: string): LinkType => {
	const regExp = /('|"|\\'|\\")detail('|"|\\'|\\"):{('|"|\\'|\\")type('|"|\\'|\\"):('|"|\\'|\\")(.+?)('|"|\\'|\\")/;
	const match = action.match(regExp);

	if (match) {
		if (Object.values(LinkType).includes(match[6] as LinkType)) {
			return match[6] as LinkType;
		} else {
			return LinkType.unsupported;
		}
	} else {
		if (action.startsWith('http')) {
			return LinkType.url;
		}

		if (action.includes('mailto:') && action.includes('@')) {
			return LinkType.mailto;
		}

		if (isInDesignNativeGoToPageAction(action)) {
			return LinkType.goToPage;
		}

		return LinkType.unsupported;
	}
};

const getActionParamRegExpPattern = (param: LinkParam): string => (
	`('|"|\\\\'|\\\\")params('|"|\\\\'|\\\\"):{('|"|\\\\'|\\\\")${param}('|"|\\\\'|\\\\"):('|"|\\\\'|\\\\")?(.+?)('|"|\\\\'|\\\\")?}`
);

export const shouldCorrectLink = (linkData: LinkData): boolean => (
	linkData.type === LinkType.goToPage && !linkData.data.correctedHref
);

// used for links inside text fields and images
export const getLinkDataFromAction = (action: string, spreads: number[][]): LinkData | null => {
	if (!action) {
		return null;
	}

	const data = getActionEventData(action);

	if (!data) {
		return null;
	}

	if (data.type === ActionEventType.goToPage) {
		return getGotoPageLinkParams(data.params.page, spreads)!;
	}

	if (data.type === ActionEventType.goToChapterId) {
		const chapterData = data as GoToChapterIdActionEventData;
		const chapterId = chapterData.params.chapterId || '';

		return {
			type: LinkType.goToChapterId,
			data: {
				href: chapterId,
			},
			originalParams: chapterData,
		};
	}

	if (data.type === ActionEventType.popup) {
		const popupData = getLinkPopupData(data as PopupActionEventData);
		if (popupData) {
			return popupData;
		}
	}

	return null;
};

const getLinkPopupData = (popupData: PopupActionEventData): LinkPopupData | null => {
	if (!popupData) {
		return null;
	}

	return {
		type: LinkType.popup,
		data: {
			item: popupData.params.subAssetDriveItem || popupData.params.assetDriveItem,
			page: popupData.params.pageNumber,
		},
		originalParams: popupData,
	};
};

export const actionEventDynamicProperty = ',"event":typeof(event) !== "undefined" ? event : undefined';

const getActionEventData = (action: string): ActionEventData | null => {
	const regExp = /.*WebPublicationAction.*({["']detail["']:.*}})\).*/;
	const getDataRegex = new RegExp(regExp);
	const regexMatch = action.match(getDataRegex);
	if (regexMatch) {
		try {
			const dataJsonString = regexMatch[1]
				.replace(actionEventDynamicProperty, '')
				.replace(/'/g, '"')
				.trim();
			const {
				type,
				params,
			} = JSON.parse(dataJsonString).detail;
			return {
				type,
				params,
			};
		} catch (error) {
			console.error('cannot parse action event data: ', action, error);
		}
	}

	return null;
};

const getActionEventString = (actionEventData: ActionEventData): string =>
	`javascript:document.dispatchEvent(new CustomEvent("WebPublicationAction", {"detail":${JSON.stringify(actionEventData)}}));return false;`;

const getDefaultPopupActionEventData = (linkPopupData: LinkPopupData): PopupActionEventData => {
	if (linkPopupData.originalParams && linkPopupData.originalParams.type === ActionEventType.popup) {
		return Utils.deepCopy(linkPopupData.originalParams);
	}

	return {
		type: ActionEventType.popup,
		params: {
			param: '',
			height: '0',
			width: '0',
			theme: 'THEATER',
			animation: null,
			header: null,
			enable: false,
			assetDriveItem: {} as Api.AspDriveResourceItem,
			subAssetDriveItem: null,
			isTooltipActive: false,
			tooltip: '',
			appearance: {
				customizationEnable: false,
				customizationUrl: null,
				style: 'THEATER',
				width: '0',
				height: '0',
				backgroundColor: '#ffffff',
				backgroundOpacity: '100',
				barBackgroundColor: '#ffffff',
				barBackgroundOpacity: '100%',
				xButtonColor: '#000000',
				xButtonColorOpacity: '100%',
			}
		},
	};
};

// used for getting link action string (on page save)
export const getLinkAction = (linkData: LinkData): string => {
	let actionEventData: ActionEventData;
	switch (linkData.type) {
		case LinkType.url:
			return linkData.data.href;
		case LinkType.goToPage:
			actionEventData = {
				type: ActionEventType.goToPage,
				params: {
					page: +linkData.data.correctedHref!,
					spread: +linkData.data.href,
				}
			} as GoToPageActionEventData;
			return getActionEventString(actionEventData);
		case LinkType.goToChapterId:
			const chapterParams = (linkData.originalParams
				? Utils.deepCopy(linkData.originalParams.params)
				: {}) as GoToChapterIdActionEventParams;

			actionEventData = {
				type: ActionEventType.goToChapterId,
				params: {
					...chapterParams,
					chapterId: linkData.data.href,
				},
			} as GoToChapterIdActionEventData;

			return getActionEventString(actionEventData);
		case LinkType.mailto:
			actionEventData = {
				type: ActionEventType.mailto,
				params: {
					email: linkData.data.href,
				}
			} as MailtoActionEventData;
			return getActionEventString(actionEventData);
		case LinkType.popup:
			actionEventData = getDefaultPopupActionEventData(linkData);
			if (actionEventData.params.subAssetDriveItem) {
				actionEventData.params.subAssetDriveItem = linkData.data.item;
				actionEventData.params.pageNumber = linkData.data.page;
			} else {
				actionEventData.params.assetDriveItem = linkData.data.item;
				actionEventData.params.pageNumber = linkData.data.page;
			}
			return getActionEventString(actionEventData);
	}
};

// used for links on buttons
export const getButtonLinkParamsFromAction = (action: string, totalPages: number, spreads: number[][]): LinkData => {
	if (!action) {
		return defaultLinkData;
	}

	const type = getLinkTypeFromAction(action);
	if (type === LinkType.url) {
		return getLinkUrlData(action);
	}

	if (type === LinkType.goToPage) {
		const data = getActionEventData(action) as GoToPageActionEventData;

		if (data) {
			return getGotoPageLinkParams(data.params.page, spreads)!;
		}

		if (isInDesignNativeGoToPageAction(action)) {
			let page = +getPageFromInDesignNativeGoToPageAction(action);
			// tslint:disable-next-line: max-line-length
			// when publication is editable a link to last page equals totalPages + 1 (because plugin creates a special service page which is removed after export but gotoPage links on buttons take into account this page)
			if (page === totalPages + 1) {
				page = page - 1;
			}

			return getGotoPageLinkParams(page, spreads)!;
		}
	}

	if (type === LinkType.goToChapterId) {
		const chapterData = getActionEventData(action) as GoToChapterIdActionEventData;

		if (chapterData) {
			const chapterId = chapterData.params.chapterId || '';

			return {
				type: LinkType.goToChapterId,
				data: {
					href: chapterId,
				},
				originalParams: chapterData,
			};
		}
	}

	if (type === LinkType.mailto) {
		const inDesignNativeEmailRegExpPattern = `mailto:(.+?)('|"|\\\\'|\\\\")?$`;
		const emailRegExp = new RegExp(`${inDesignNativeEmailRegExpPattern}|${getActionParamRegExpPattern(LinkParam.email)}`);
		const match = action.match(emailRegExp);

		if (match) {
			return {
				type: LinkType.mailto,
				data: {
					href: decodeURIComponent(match[1] || match[8]),
					correctedHref: '',
				},
			};
		}
	}

	if (type === LinkType.popup) {
		const actionEventData = getActionEventData(action);
		if (actionEventData) {
			const data = getLinkPopupData(actionEventData as PopupActionEventData);
			if (data) {
				return data;
			}
		}
	}

	return defaultLinkData;
};

const isInDesignNativeGoToPageAction = (action: string): boolean => {
	const regExp = /.+?-(\d+).xhtml/;
	return !!action.match(regExp);
};

const getPageFromInDesignNativeGoToPageAction = (action: string): string => {
	const inDesignNativeGoToPageRegExp = /.+?-(\d+)$/;
	// format of inDesign native gotoPage action: ${publicationName}-${page}.xhtml
	const splittedAction = action.split('.');
	const actionPartToCheck = splittedAction[splittedAction.length - 2];
	const inDesignNativeGoToPageMatch = actionPartToCheck && actionPartToCheck.match(inDesignNativeGoToPageRegExp);

	return inDesignNativeGoToPageMatch ? inDesignNativeGoToPageMatch[1] : '';
};

const getGotoPageLinkParams = (page: number, spreads: number[][], spread?: number): LinkGoToPageData | null => {
	if (page) {
		if (spread) {
			return {
				type: LinkType.goToPage,
				data: {
					href: page.toString(),
					correctedHref: spread.toString(),
				},
			};
		}

		const spreadIndex = spreads.findIndex((spread) => spread.includes(page));
		return {
			type: LinkType.goToPage,
			data: {
				href: (spreadIndex + 1).toString(),
				correctedHref: page.toString(),
			},
		};
	}

	return null;
};

export interface PositionAndSize {
	width: number;
	height: number;
	x: number;
	y: number;
}

export const getImageCenteredPositionAndSize = (
	frameWidth: number,
	frameHeight: number,
	imageWidth: number,
	imageHeight: number,
	isPercentageValues?: boolean
): PositionAndSize => {
	let width = imageWidth;
	let height = imageHeight;
	let scale = 1;

	const isImageWiderThanFrame = imageWidth > frameWidth;

	if (isImageWiderThanFrame) {
		width = frameWidth;
		scale = frameWidth / imageWidth;
		height = imageHeight * scale;
	}

	const isImageHigherThanFrame = height > frameHeight;

	if (isImageHigherThanFrame) {
		height = frameHeight;
		scale = frameHeight / imageHeight;
		width = imageWidth * scale;
	}

	const centeredX = (frameWidth - width) / 2;
	const centeredY = (frameHeight - height) / 2;

	return {
		width: getRoundedValue(isPercentageValues ? (width / frameWidth * 100) : width),
		height: getRoundedValue(isPercentageValues ? (height / frameHeight * 100) : height),
		x: getRoundedValue(isPercentageValues ? (centeredX / frameWidth * 100) : centeredX),
		y: getRoundedValue(isPercentageValues ? (centeredY / frameHeight * 100) : centeredY),
	};
};

export const getOptimizedImage = (driveItem: Api.DriveFolderInfo, width: number, height: number, maxWidth: number): OptimizedImageData => {
	const { driveList } = store.getState();
	const { associatedResolutions, id, src, name } = driveItem;
	const resolutions = driveItem.resolutions || driveList.resolutions;
	const optimizedImage = {
		width,
		height,
		src,
	};

	if (associatedResolutions) {
		const availableResolutionsId = associatedResolutions[id] || 'default';

		if (resolutions[availableResolutionsId]) {
			const optimizedSize = [...resolutions[availableResolutionsId].sizes]
				.sort((a, b) => a.width - b.width)
				.find(({ width }) => width >= maxWidth);

			if (optimizedSize) {
				const fileExtension = Utils.getFileExtension(name);
				optimizedImage.height = height * (optimizedSize.width / width);
				optimizedImage.width = optimizedSize.width;
				optimizedImage.src = src.replace(`.${fileExtension}`, `_${optimizedSize.box ? 'box-' : ''}${optimizedSize.width}x${optimizedSize.height}.${fileExtension}`);
			}
		}
	}

	return optimizedImage;
};

export const getStringWithoutSpaces = (str: string): string => (
	str.trim().replace(/>\s+</g, '><')
);

export const isStyleIncludesHeight = (style: string): boolean => {
	const styleWithoutSpaces = style.replace(/\s+/g, '');
	return styleWithoutSpaces.includes('height') && (
		styleWithoutSpaces.indexOf('height') === 0 ||
		styleWithoutSpaces[styleWithoutSpaces.indexOf('height') - 1] === ';'
	);
};

export const isEditableButtonLink = (linkElement: HTMLAnchorElement): boolean => (
	[...linkElement.children].some(child => (
		child.className.includes(constants.editableButtonLinkGraphicsClassName)
	))
);

export const getStylesArrayWithoutSpaces = (styles: string): string[] => (
	styles.split(';').reduce((acc: string[], current: string) => {
		if (current) {
			const [key, value] = current.split(':');
			acc.push(`${key.trim()}:${value.trim()}`);
		}
		return acc;
	}, [])
);

export const getStylesStringWithoutSpaces = (styles: string): string => (
	styles.split(';').reduce((acc: string, current: string) => {
		if (current) {
			const [key, value] = current.split(':');
			acc += (`${key.trim()}:${value.trim()};`);
		}
		return acc;
	}, '')
);

export const getStylesStringWithoutLastSemicolon = (styles: string): string => (
	styles[styles.length - 1] === ';' ? styles.slice(0, -1) : styles
);

interface DataAttributes {
	[k: string]: string | number | boolean | undefined;
}

export const setDataAttributes = (element: HTMLElement, attributes: DataAttributes): void => {
	Object.entries(attributes).forEach(([key, value]) => {
		if (value) {
			element.setAttribute(key, value.toString());
		}
	});
};

export const getElementId = (element: HTMLElement): string => {
	const parentElement = element.closest(`[id*=${constants.parentContainerId}]`) as HTMLElement;
	return parentElement.id;
};

export const hasLockedParent = (element: HTMLElement): boolean => (
	!!element.closest(`[class*=${constants.lockedClassName}]`)
);

export const getDependencyString = (globalId: number) => `'\${media}${globalId}'`;

export const getPublicationPdfImportingId = (globalId: number, status: PdfImportingStatus) => (
	`${globalId}-pdf-${status}`
);

export const isDuplicatedItemInSpread = (className: string, pageIndexInSpread: number): boolean => {
	const pageIndexInClassNameRegExp = new RegExp(`${constants.pageIndexInSpreadClassName}-(\\d{1})`);
	const match = className.match(pageIndexInClassNameRegExp);

	if (!match) {
		// for backward compatibility
		return false;
	}

	return pageIndexInSpread !== +match[1];
};

export const getClientAndResourceIds = (): {
	clientId: number,
	originalGId: number,
	tempGId: number,
} => {
	const {
		routerParamsHistory,
		editor: { preprodPublication: { tempGId } },
	} = store.getState();
	const { params: { clientId, resourceId } } = routerParamsHistory[routerParamsHistory.length - 1];

	return {
		clientId: +clientId,
		originalGId: +resourceId,
		tempGId: tempGId || +resourceId,
	};
};

export const isElementOverlapped = (element: HTMLElement): boolean => {
	const style = document.createElement('style');
	const styleId = 'editor-temp-styles';
	style.id = styleId;
	// is needed for correct defining of top most element (elementFromPoint doesn't take into account elements with pointer-events: none)
	const tempStyles = `${PAGE_CONTENT_CLASSNAME} > div * { pointer-events: auto !important; }`;
	style.innerHTML = tempStyles;
	document.head.appendChild(style);

	const { top, left, width, height } = element.getBoundingClientRect();
	const centeredX = left + width / 2;
	const centeredY = top + height / 2;
	const topMostElement = document.elementFromPoint(centeredX, centeredY);
	document.head.removeChild(document.querySelector(`#${styleId}`) as Element);

	return !element.contains(topMostElement);
};

export const isElementHidden = (element: HTMLElement): boolean => {
	const { display, opacity, visibility } = window.getComputedStyle(element);

	return display === 'none' ||
		opacity === '0' ||
		visibility === 'hidden';
};

const verticalAlignClassNameRegExp = /(vertical-align-.+?)(\s|-|$)/;

export const getVerticalAlignClassName = (className: string): VerticalAlignClassName | '' => {
	const match = className.match(verticalAlignClassNameRegExp);

	if (!match) {
		return '';
	}

	return match[1] as VerticalAlignClassName;
};

export const setVerticalAlignClassName = (element: HTMLElement, newClassName: VerticalAlignClassName | ''): void => {
	const match = element.className.match(verticalAlignClassNameRegExp);
	const currentVerticalAlignClassName = match ? match[1] : '';

	if (currentVerticalAlignClassName) {
		element.className = element.className.replace(currentVerticalAlignClassName, newClassName);
	} else {
		if (newClassName) {
			element.classList.add(newClassName);
		}
	}
};

export const isTextScriptElement = (className: string): boolean => (
	className.includes(superScriptClassName) || className.includes(subScriptClassName)
);

export const getTextScriptClassName = (className: string): string => (
	className.includes(superScriptClassName) ? superScriptClassName : subScriptClassName
);

export interface SelectedSpreads {
	spreadIndices: number[];
	lastSelectedSpreadIndex: number;
	lastSelectedShiftSpreadIndex: number;
}

export const getSelectedSpreads = (
	spreadIndex: number,
	allSpreads: number[][],
	prevSelectedSpreadIndices: number[],
	prevLastSelectedSpreadIndex: number,
	prevLastSelectedShiftSpreadIndex: number,
	isCtrlPressed?: boolean,
	isShiftPressed?: boolean,
	isMultiSelection?: boolean,
): SelectedSpreads => {
	let selectedSpreadIndices: number[] = [];
	let lastSelectedSpreadIndex: number = -1;
	let lastSelectedShiftSpreadIndex: number = -1;

	if (isCtrlPressed) {
		selectedSpreadIndices = getSelectedSpreadIndices(spreadIndex, prevSelectedSpreadIndices);
		lastSelectedSpreadIndex = spreadIndex;
	} else if (isShiftPressed) {
		if (prevSelectedSpreadIndices.length === 0) {
			selectedSpreadIndices = [spreadIndex];
			lastSelectedSpreadIndex = spreadIndex;
		} else {
			const allSpreadIndices = Array.from({ length: allSpreads.length }, (v, i) => i);
			SelectionManager.set(prevSelectedSpreadIndices, allSpreadIndices);

			if (prevLastSelectedShiftSpreadIndex > -1) {
				SelectionManager.deselectItems(prevLastSelectedShiftSpreadIndex, prevLastSelectedSpreadIndex);
			}

			SelectionManager.selectItems(spreadIndex, prevLastSelectedSpreadIndex);
			selectedSpreadIndices = SelectionManager.get();
			lastSelectedSpreadIndex = prevLastSelectedSpreadIndex;
			lastSelectedShiftSpreadIndex = spreadIndex;
		}
	} else {
		if (isMultiSelection) {
			selectedSpreadIndices = getSelectedSpreadIndices(spreadIndex, prevSelectedSpreadIndices);
		} else {
			selectedSpreadIndices = [spreadIndex];
		}

		lastSelectedSpreadIndex = spreadIndex;
	}

	return {
		spreadIndices: selectedSpreadIndices,
		lastSelectedSpreadIndex,
		lastSelectedShiftSpreadIndex,
	};
};

const getSelectedSpreadIndices = (spreadIndex: number, prevSelectedSpreadIndices: number[]): number[] => (
	prevSelectedSpreadIndices.includes(spreadIndex)
		? prevSelectedSpreadIndices.filter(index => index !== spreadIndex)
		: [...prevSelectedSpreadIndices, spreadIndex].sort((a, b) => a - b)
);

export const getSliderPreviewUrl = (
	config: Api.Config,
	relUrl: string,
	isExternalLink: boolean,
	externalRelUrl: string,
	iconName?: string | null,
): string => {
	return isExternalLink
		? getYoutubeVideoCover(externalRelUrl)
		: getResourceUrl(iconName || (hasMediaParam(relUrl) ? relUrl : relUrl.split('/')[1]) || '', config);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/helpers.ts